import { useRouter } from 'next/router';
import { useRef, useEffect, useContext } from 'react';
import { removeNullUndefinedEmptyFromObject } from '../helpers/common';
import { fetchSearchResults } from '../new-lib/apis';
import { trackEvent } from '../new-lib/remarketing';
import { SearchFiltersActionTypes } from '../reducers/search-filters/actions';
import {
  SearchContext,
  SearchContextInterface
} from '../features/search-new/context/SearchContext';
import { useDispatch } from 'react-redux';
import { setSearchResults } from '../redux/slices/searchResultsSlice';
import {
  NewCompoundSearchFiltersTypes,
  SearchBodyInterface,
  SearchResults
} from '../features/search-new/types';
import { useQuery } from 'react-query';
import { SearchViewTypes } from '../new-lib/constants/common';
import { SearchApis } from '../new-lib/apis/search.apis';

const useSearchResults = (
  searchBody: any,
  loadedSearchResultsSSR?: any
): SearchBodyInterface => {
  const dispatch = useDispatch();
  const { locale, query } = useRouter();
  const firstRenderSSR = useRef(!!loadedSearchResultsSSR);

  const { searchFiltersState, searchFiltersDispatch } = useContext(
    SearchContext
  ) as SearchContextInterface;
  const router = useRouter();

  const isPropertySearch =
    searchBody.show == 'property' ||
    query?.category == 'property' ||
    router.asPath.includes('properties');

  const searchFiltersToMapFilters: any = {
    sort_by: NewCompoundSearchFiltersTypes.orderBy,
    areas: NewCompoundSearchFiltersTypes.areas,
    developers: NewCompoundSearchFiltersTypes.developers,
    property_types: NewCompoundSearchFiltersTypes.propertyTypes,
    amenities: NewCompoundSearchFiltersTypes.amenities,
    bedrooms: NewCompoundSearchFiltersTypes.bedrooms,
    bathrooms: NewCompoundSearchFiltersTypes.bathrooms,
    finishing: NewCompoundSearchFiltersTypes.finishing,
    min_price: NewCompoundSearchFiltersTypes.minPrice,
    max_price: NewCompoundSearchFiltersTypes.maxPrice,
    min_unit_area: NewCompoundSearchFiltersTypes.minUnitArea,
    max_unit_area: NewCompoundSearchFiltersTypes.maxUnitArea,
    delivery_date: NewCompoundSearchFiltersTypes.minReadyBy,
    delivery_years: NewCompoundSearchFiltersTypes.readyByYears,
    down_payment: NewCompoundSearchFiltersTypes.maxDownPayment,
    monthly_installments: NewCompoundSearchFiltersTypes.maxInstallments,
    installment_years: NewCompoundSearchFiltersTypes.installmentYears,
    sale_type: NewCompoundSearchFiltersTypes.saleType,
    has_garden: NewCompoundSearchFiltersTypes.hasGarden,
    has_roof: NewCompoundSearchFiltersTypes.hasRoof,
    nawy_now: NewCompoundSearchFiltersTypes.isNawyNow
  };

  const newCompoundSearchKeys = () => {
    return Object.keys(searchFiltersState.searchFilters).reduce(
      (acc: any, key) => {
        const mappedKey = searchFiltersToMapFilters[key];

        const getValue = () => {
          if (mappedKey == 'minReadyBy') {
            const oldVal =
              searchFiltersState.searchFilters[
                key as keyof typeof searchFiltersState.searchFilters
              ];
            if (oldVal) {
              if (typeof oldVal === 'string') {
                return new Date(oldVal).getFullYear();
              }
              return undefined;
            }
          }
          if (mappedKey == 'orderBy') {
            const sortValue: any =
              searchFiltersState.searchFilters[
                key as keyof typeof searchFiltersState.searchFilters
              ];
            if (sortValue.order_by) {
              return `${sortValue.order_by}:${sortValue.order_direction}`;
            }
            return undefined;
          } else {
            return searchFiltersState.searchFilters[
              key as keyof typeof searchFiltersState.searchFilters
            ];
          }
        };
        const value: any = getValue();

        if (
          mappedKey &&
          value &&
          (Array.isArray(value) ? value.length > 0 : true)
        ) {
          acc[mappedKey] = value;
        }
        return acc;
      },
      {}
    );
  };

  const { isFetching, isError, data, isLoading } = useQuery({
    initialData: loadedSearchResultsSSR || [],
    queryKey: ['searchResults', searchBody, locale],
    queryFn: async ({ signal }) =>
      isPropertySearch
        ? fetchSearchResults(
            removeNullUndefinedEmptyFromObject(searchBody),
            locale,
            { signal }
          )
        : SearchApis.newCompoundsSearch(
            removeNullUndefinedEmptyFromObject({
              page: searchFiltersState.page_number,
              pageSize: 12,
              ...newCompoundSearchKeys()
            }),
            locale,
            { signal }
          ),
    refetchOnWindowFocus: false,
    enabled:
      !firstRenderSSR.current && router.query?.view !== SearchViewTypes.MAP
  });
  useEffect(() => {
    if (
      Object.keys(searchBody).length != 2 ||
      searchBody.show != 'compound' ||
      searchBody.start != 1
    ) {
      firstRenderSSR.current = false;
    }
  }, [searchBody]);
  useEffect(() => {
    if (!isFetching && data) {
      trackEvent.search({
        listingIds: data.data?.values.map((result: any) => result.id)
      });
    }
  }, [data]);

  useEffect(() => {
    if (
      searchFiltersState < searchBody?.start &&
      searchFiltersState.page_number &&
      searchFiltersState.page_number > 1
    ) {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.UPDATE_PAGE_NUMBER,
        payload: {
          name: 'page_number',
          value: 1
        }
      });
    }
  }, [searchBody]);

  const getLoadedSearchResults = (): SearchResults => {
    if (firstRenderSSR.current) {
      return isPropertySearch
        ? loadedSearchResultsSSR.values
        : loadedSearchResultsSSR.results;
    } else if (isFetching) {
      return null;
    } else {
      return isPropertySearch ? data?.data?.values : data?.results;
    }
  };

  const getLoadedSearchResultsCount = () => {
    if (firstRenderSSR.current) {
      return isPropertySearch
        ? loadedSearchResultsSSR.total_properties
        : loadedSearchResultsSSR.total;
    } else {
      return isPropertySearch ? data?.data?.total_properties : data?.total;
    }
  };
  const searchBodyResults: SearchBodyInterface = {
    isFetching: firstRenderSSR.current ? false : isFetching,
    isLoading: isLoading,
    isError: isError,
    loadedSearchResults: getLoadedSearchResults(),
    loadedSearchResultsCount: getLoadedSearchResultsCount(),
    loadedPropertyTypes: loadedSearchResultsSSR
      ? loadedSearchResultsSSR.property_types
      : data?.data?.property_types,
    loadedAmenities: loadedSearchResultsSSR
      ? loadedSearchResultsSSR.amenities
      : data?.data?.amenities,
    firstRenderSSR: firstRenderSSR.current,
    ssrSearchType: loadedSearchResultsSSR
      ? loadedSearchResultsSSR?.total_properties > 0
        ? 'properties'
        : 'compounds'
      : isPropertySearch
        ? 'properties'
        : 'compounds'
  };
  dispatch(setSearchResults(searchBodyResults));
  return searchBodyResults;
};

export default useSearchResults;
